import { Button, Drawer } from '@toggle/design-system';
import { Entity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { WithFooterWrapper } from '~/components/with-footer-wrapper/WithFooterWrapper';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import {
  CompetitorData,
  useCompetitorsData,
} from '~/widgets/knowledge-graph-widget/hooks/useCompetitorsData';

import { CompetitorsWidget } from '../competitors-widget/CompetitorsWidget';
import { useCompetitorsDrawer } from './useCompetitorsDrawer';

const buildFallbackCompetitorData = (
  entity: Entity,
  company: string
): CompetitorData => ({
  entity,
  competitorData: {
    company,
    relationship: {
      id: '',
      description: '',
      rank: 0,
      rank_change: null,
      evidence: null,
      source_evidence: null,
    },
    competing_themes: [],
  },
  competingThemes: [],
});

export interface CompetitorsDrawerProps {
  entity: Entity;
  fallbackCompetitorEntity?: Entity;
}

export const CompetitorsDrawer = ({
  entity,
  fallbackCompetitorEntity,
}: CompetitorsDrawerProps) => {
  const { t } = useTranslation(['widget']);
  const { navigateToPage } = useNavigateToAssetOverview();
  const { closeDrawer, isDrawerOpen, competitorTag } = useCompetitorsDrawer();

  const { selectedCompetitor, competitorsByTag } = useCompetitorsData({
    entityTag: entity.tag,
  });

  if (!selectedCompetitor) {
    return null;
  }

  const competitor =
    competitorsByTag[selectedCompetitor] ??
    (fallbackCompetitorEntity
      ? buildFallbackCompetitorData(fallbackCompetitorEntity, selectedCompetitor)
      : undefined);

  if (!competitor) {
    return null;
  }

  return (
    <Drawer
      data-testid="competitors-drawer"
      isOpen={isDrawerOpen}
      handleCloseClick={closeDrawer}
      headerButton={
        competitorTag ? (
          <Button
            variant="empty"
            size="xsmall"
            label={t('themeOverview.drawer.assetOverview')}
            iconName="Expand"
            onClick={() => navigateToPage({ entityTag: competitorTag })}
          />
        ) : null
      }
    >
      <WithFooterWrapper
        isWrappedComponentOpened={isDrawerOpen}
        trackingMetadata={{
          source: POSTHOG_TRACKING_SOURCES.COMPETITORS_DRAWER,
        }}
      >
        <CompetitorsWidget entity={entity} competitorData={competitor} />
      </WithFooterWrapper>
    </Drawer>
  );
};
